/**
 * Service: Website enquiries
 *
 * §2c — there is no longer a separate `enquiries` table. A website enquiry IS a
 * support ticket with source='website_enquiry' (the enum value was reserved for
 * exactly this), so it lands in the one agent queue with the one workflow.
 *
 * Enquirers are anonymous: `create_support_ticket` is SECURITY DEFINER and
 * anon-executable, it captures requester_email, applies the server-side
 * priority floor (business => p2 — a client hint can only RAISE it), and
 * returns the access_token that lets the enquirer follow the thread.
 *
 * The returned access_token is a BEARER SECRET. Hand it to the thread store,
 * never to a log, a URL we render, or an analytics call.
 */
import { supabase } from "@/integrations/supabase/client";

/** A general partnership/press enquiry is business, not a support incident. */
const ENQUIRY_CATEGORY = "business" as const;

export interface EnquiryTicket {
  ticketId: string;
  accessToken: string;
  priority: string;
  status: string;
}

export async function createEnquiry(payload: {
  /** Organisation the enquirer represents. No column for it on a ticket, so it
   *  is carried in the subject and restated in the body rather than dropped. */
  company_name: string;
  contact_name: string;
  email: string;
  message?: string | null;
}): Promise<{ data: EnquiryTicket | null; error: { message: string } | null }> {
  const org = payload.company_name.trim();
  const note = (payload.message ?? "").trim();

  // The body must stand alone in the agent console: an agent reading the queue
  // sees who wrote in, for whom, and what they said, without a second lookup.
  const body = [
    `Website enquiry from ${payload.contact_name.trim()} at ${org}.`,
    note ? `\n${note}` : "\n(No message provided.)",
  ].join("");

  const { data, error } = await supabase.rpc("create_support_ticket", {
    _subject: `Partnership enquiry — ${org}`.slice(0, 200),
    _body: body,
    _requester_email: payload.email.trim(),
    _category: ENQUIRY_CATEGORY,
    _requester_name: payload.contact_name.trim(),
    _source: "website_enquiry",
    _ai_handled: false,
  });

  if (error) return { data: null, error: { message: error.message } };

  const row = Array.isArray(data) ? data[0] : data;
  if (!row?.o_ticket_id || !row?.o_access_token) {
    return { data: null, error: { message: "Enquiry could not be recorded." } };
  }

  return {
    data: {
      ticketId: row.o_ticket_id as string,
      accessToken: row.o_access_token as string,
      priority: row.o_priority as string,
      status: row.o_status as string,
    },
    error: null,
  };
}

/** Enquiry volume for the platform analytics tile, now read off the queue. */
export async function getEnquiryCount() {
  return supabase
    .from("support_tickets")
    .select("id", { count: "exact", head: true })
    .eq("source", "website_enquiry");
}
